import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { MasajidContext } from "../contexts/MasajidContext";
import Fuse from "fuse.js";
import { Heart, UserStar, MapPin } from "lucide-react";
import BottomNavBar from "./BottomNavBar";

function Main() {
  const { masajid, getMyMasjid, language } = useContext(MasajidContext);
  const [search, setSearch] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [suggestions, setSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [offset, setOffset] = useState(0);
  const inputRef = useRef(null);
  const titleRef = useRef(null);
  const heroContentRef = useRef(null);
  const navigate = useNavigate();

  const handleFocus = () => {
    setIsFocused(true);

    // Get initial position immediately before any viewport changes
    if (!titleRef.current) return;

    const initialRect = titleRef.current.getBoundingClientRect();
    const padding = 16;
    const targetOffset = -(initialRect.top - padding);

    if (isMobile) {
      // On mobile, apply offset immediately before keyboard opens
      setOffset(targetOffset);
    } else {
      // On desktop, apply offset immediately
      setOffset(targetOffset);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setSuggestions([]);
      return;
    }

    const fuse = new Fuse(masajid, {
      keys: ["engName", "urdName"],
      threshold: 0.3,
    });

    const results = fuse.search(search);
    const matched = results
      .map((r) => r.item)
      .sort((a, b) => a.engName.localeCompare(b.engName));

    setSuggestions(matched.slice(0, 10));
  }, [search, masajid]);

  const handleSearch = () => {
    if (search.trim()) {
      setSuggestions([]);
      setIsFocused(false);
      navigate(`/search-results?q=${encodeURIComponent(search)}`);
    }
  };

  const handleSuggestionClick = (masjid) => {
    setSearch("");
    setSuggestions([]);
    setIsFocused(false);
    navigate(`/masjid/${masjid.username}`);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsFocused(false);
      setOffset(0);
    }, 200);
  };

  return (
    <>
      <section className="hero-section">
        <div
          ref={heroContentRef}
          className={`hero-content ${isFocused ? "moved-up" : ""}`}
          style={{
            transform: isFocused ? `translateY(${offset}px)` : "translateY(0)",
            width: "100%",
            maxWidth: "800px",
          }}
        >
          <img id="hero-logo" src="/assets/img/logo.png" alt="MIS Logo" />
          <h1
            ref={titleRef}
            className="hero-title"
            lang={language === "urdu" && "ur"}
            style={{ textAlign: "center", color: "#ffffff" }}
          >
            {language === "urdu" && "مساجد "}
            <span
              style={{
                color: "rgb(27, 179, 200)",
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              <strong>MIS</strong>
            </span>{" "}
            {language === "english" && "Masajid"}
          </h1>

          {!isOnline && (
            <div style={{ marginTop: 64, textAlign: "center" }}>
              <h1
                style={{ color: "#f63c3c", marginTop: 64 }}
                lang={language === "urdu" && "ur"}
              >
                {language === "urdu"
                  ? "آپ کا انٹرنیٹ کام نہیں کر رہا"
                  : "Please connect to the internet"}
              </h1>
            </div>
          )}

          {isOnline && (
            <>
              <div
                className="d-flex d-md-flex justify-content-center justify-content-md-center"
                style={{
                  marginTop: 24,
                  width: "95%",
                  maxWidth: "800px",
                }}
              >
                <div
                  className="input-group"
                  style={{
                    background: "#05212b",
                    borderRadius: "40px",
                    border: "3px solid rgb(27, 179, 200)",
                    display: "flex",
                    alignItems: "center",
                    padding: "8px",
                  }}
                >
                  {language === "urdu" && (
                    <span
                      className="input-group-text input-group-append"
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                      }}
                    >
                      <button
                        className="search-button"
                        type="button"
                        onClick={handleSearch}
                      >
                        <i
                          className="fas fa-search"
                          style={{ color: "#ffffff" }}
                        />
                      </button>
                    </span>
                  )}
                  <input
                    ref={inputRef}
                    className="py-2 mr-1 pr-5 search-bar"
                    lang={language === "urdu" && "ur"}
                    type="search"
                    placeholder={
                      language === "urdu" ? "مسجد کا نام" : "Masjid Name"
                    }
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyUp={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    dir={language === "urdu" ? "rtl" : "ltr"}
                  />
                  {language === "english" && (
                    <span
                      className="input-group-text input-group-append"
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                      }}
                    >
                      <button
                        className="search-button"
                        type="button"
                        onClick={handleSearch}
                      >
                        <i
                          className="fas fa-search"
                          style={{ color: "#ffffff" }}
                        />
                      </button>
                    </span>
                  )}
                  {isFocused && suggestions.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: "#041b23",
                        border: "3px solid #041b23",
                        borderRadius: 12,
                        maxHeight: 300,
                        overflowY: "auto",
                        zIndex: 9999,
                        marginTop: 4,
                      }}
                    >
                      {suggestions.map((masjid) => (
                        <div
                          key={masjid.username}
                          onClick={() => handleSuggestionClick(masjid)}
                          className="suggestion"
                        >
                          <p
                            style={{
                              margin: 0,
                              textAlign:
                                language === "urdu" ? "right" : "left",
                              fontSize: "1.1rem",
                              color: "rgb(255, 255, 255, 0.8)",
                            }}
                            lang={language === "urdu" && "ur"}
                            dir={language === "urdu" ? "rtl" : "ltr"}
                          >
                            {language === "urdu"
                              ? masjid.urdName
                              : masjid.engName}
                          </p>
                          <p
                            style={{
                              margin: "2px 0 0 0",
                              fontSize: "0.9rem",
                              color: "rgb(255, 255, 255, 0.5)",
                              textAlign:
                                language === "urdu" ? "right" : "left",
                            }}
                            lang={language === "urdu" && "ur"}
                            dir={language === "urdu" ? "rtl" : "ltr"}
                          >
                            {language === "urdu"
                              ? masjid.addressUrdu
                              : masjid.address}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Action Buttons */}
              <div
                style={{
                  marginTop: 20,
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  width: isMobile ? "80%" : "95%",
                  maxWidth: "800px",
                }}
              >
                {getMyMasjid() && (
                  <button
                    onClick={() => navigate(`/masjid/${getMyMasjid()}`)}
                    style={{
                      flex: isMobile ? "unset" : 1,
                      width: isMobile ? "100%" : "auto",
                      backgroundColor: "rgba(34, 197, 94, 0.1)",
                      border: "2px solid #22c55e",
                      borderRadius: "25px",
                      padding: "12px 24px",
                      color: "rgb(255, 255, 255, 0.9)",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      position: "relative",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "rgba(34, 197, 94, 0.2)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "rgba(34, 197, 94, 0.1)";
                    }}
                  >
                    <UserStar size={18} color="#22c55e" />
                    <span lang={language === "urdu" && "ur"}>
                      {language === "urdu" ? "میری مسجد" : "My Masjid"}
                    </span>
                  </button>
                )}

                <button
                  onClick={() => navigate("/favourites")}
                  style={{
                    flex: isMobile ? "unset" : 1,
                    width: isMobile ? "100%" : "auto",
                    backgroundColor: "rgba(230, 57, 70, 0.1)",
                    border: "2px solid #e63946",
                    borderRadius: "25px",
                    padding: "12px 24px",
                    color: "rgb(255, 255, 255, 0.9)",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    position: "relative",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(230, 57, 70, 0.2  )";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(230, 57, 70, 0.1)";
                  }}
                >
                  <Heart size={18} color="#e63946" />
                  <span lang={language === "urdu" && "ur"}>
                    {language === "urdu"
                      ? "پسندیدہ مساجد"
                      : "Favourite Masajid"}
                  </span>
                </button>

                <button
                  onClick={() => navigate("/nearby")}
                  style={{
                    flex: isMobile ? "unset" : 1,
                    width: isMobile ? "100%" : "auto",
                    backgroundColor: "rgba(27, 179, 200, 0.1)",
                    border: "2px solid rgb(27, 179, 200)",
                    borderRadius: "25px",
                    padding: "12px 24px",
                    color: "rgb(255, 255, 255, 0.9)",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    position: "relative",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(27, 179, 200, 0.2)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(27, 179, 200, 0.1)";
                  }}
                >
                  <MapPin size={18} color="rgb(27, 179, 200)" />
                  <span lang={language === "urdu" && "ur"}>
                    {language === "urdu"
                      ? "قریبی مساجد ڈھونڈیں"
                      : "Find Nearby Masajid"}
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </section>
      <BottomNavBar />
    </>
  );
}

export default Main;
